// Styles
import styled from 'styled-components/macro'

// Core
import React, {Fragment, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {useHistory, useLocation} from 'react-router-dom'

// Components, Services and Constants
import AdditionalDetailsCard from 'components/AdditionalDetailsCard'
import PropertyDetailsCard from 'components/PropertyDetailsCard'
import PropertyOverviewHero from 'components/PropertyOverviewHero'

import {resetPropertyState} from 'actions/propertyActions'
import {ERROR} from 'constants/reduxStatuses'
import {
  additionalDetailsSelector,
  isPropertyDataLoadingSelector,
  propertyDetailsSelector,
  propertyHeroDataSelector,
  propertyPidSelector,
  propertyStatusSelector
} from 'selectors'

// 3rd-party
import CircularProgress from '@material-ui/core/CircularProgress'
import PropertyProductsList from './PropertyProductsList'

const Property = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const history = useHistory()
  const additionalDetails = useSelector(additionalDetailsSelector)
  const propertyDetails = useSelector(propertyDetailsSelector)
  const propertyHeroData = useSelector(propertyHeroDataSelector)
  const isPropertyDataLoading = useSelector(isPropertyDataLoadingSelector)
  const pid = useSelector(propertyPidSelector)

  const propertyStatus = useSelector(propertyStatusSelector)

  useEffect(() => {
    const isError = propertyStatus === ERROR
    if (isError) {
      // we use history.replace here to ensure that clicking the Go Back button on the
      // NotFound will actually take the user to the previous route and avoids the continuous
      // loop that occurs if we use history.push instead
      history.replace({
        pathname: location.pathname,
        state: {
          error: true
        }
      })
    }
  }, [dispatch, history, location, propertyStatus])

  // Clear property slice of redux when navigating away from page. This ensures
  // that any errors that might have come as a result of a bad pid are cleared
  // and good pids will still load after the fact.
  useEffect(() => {
    return () => {
      dispatch(resetPropertyState())
    }
  }, [dispatch])

  return (
    <Property.Styled $isPropertyDataLoading={isPropertyDataLoading}>
      {isPropertyDataLoading ? (
        <div className='loading-wrap'>
          {/* inner div needed for IE11 */}
          <div>
            <CircularProgress size={100} />
          </div>
        </div>
      ) : (
        <Fragment>
          <PropertyOverviewHero subjectData={propertyHeroData} pid={pid} />
          <div className='property-details-section'>
            <Fragment>
              <PropertyDetailsCard selectorData={propertyDetails} className='details-card' />
              <AdditionalDetailsCard selectorData={additionalDetails} className='details-card' />
            </Fragment>
          </div>
          <PropertyProductsList pid={pid} />
        </Fragment>
      )}
    </Property.Styled>
  )
}

Property.Styled = styled.div<{$isPropertyDataLoading?: boolean}>`
  height: ${({$isPropertyDataLoading}) => ($isPropertyDataLoading ? '100%' : 'auto')};
  display: ${({$isPropertyDataLoading}) => ($isPropertyDataLoading ? 'flex' : 'block')};
  flex: none;
  flex-direction: column;

  .property-details-section {
    display: flex;
    flex: 1 0 100%;
    max-width: 1360px;
    justify-content: space-between;
    align-items: center;
    padding: 40px 0 44px;
    margin: 0 auto;

    @media (max-width: 1360px) {
      padding-left: 40px;
      padding-right: 40px;
    }
  }

  @media (max-width: 1024px) {
    .property-details-section {
      flex-wrap: wrap;

      .details-card {
        flex-basis: 100%;
        margin-left: 0;

        &:first-of-type {
          margin-bottom: 40px;
        }
      }
    }
  }

  .cards-wrap {
    margin-bottom: 40px;
  }
`

export default Property
